import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useApiClient } from './useApiClient';
import { showApiErrorToast } from './util/commonMutation';
import type { UserMasterInfo, UserMasterCreateRequest, UserMasterUpdateRequest } from '../types';
import toast from 'react-hot-toast';

/** * ユーザー管理共通のクエリキー
 * キャッシュの無効化（invalidateQueries）時に使用します。
 */
const USER_LIST_KEY = ['masters_reference', 'user'];

/**
 * ユーザー管理 API定義フック
 * 各種エンドポイント（取得・登録・更新・削除・パスワードリセット）へのリクエストロジックを提供します。
 */
export const useUsersApi = () => {
  const api = useApiClient();

  /** 全ユーザー情報の取得（プロファイル含む） */
  const getUsersWithProfile = useCallback(async () => {
    const response = await api.get<UserMasterInfo[]>('/masters/users/all');
    
    if (Array.isArray(response.data)) {
      return response.data.sort((a, b) => {
        // 1. まずマスターコード (masterCode) で比較
        // localeCompareを使用することで、数値混じりのIDも自然な順序で並ぶ
        // numeric: true を指定すると 2 < 10 と判定される
        const masterCompare = String(a.masterCode || '').localeCompare(String(b.masterCode || ''), undefined, { 
          numeric: true, 
          sensitivity: 'base' 
        });
  
        // 2. マスターコードが異なる場合は、その結果を返す
        if (masterCompare !== 0) {
          return masterCompare;
        }
  
        // 3. マスターコードが同じ場合は、ログインID (loginId) で比較
        return String(a.loginId || '').localeCompare(String(b.loginId || ''), undefined, { 
          numeric: true, 
          sensitivity: 'base' 
        });
      });
    }
    
    return response.data;
  }, [api]);

  /** 新規ユーザー登録 */
  const createUser = useCallback(async (req: UserMasterCreateRequest) => {
    const response = await api.post<UserMasterInfo>('/masters/users', req);
    return response.data;
  }, [api]);

  /** 既存ユーザー情報の更新 */
  const updateUser = useCallback(async (id: string, req: UserMasterUpdateRequest) => {
    const response = await api.put<UserMasterInfo>(`/masters/users/${id}`, req);
    return response.data;
  }, [api]);

  /** ユーザーの削除 */
  const deleteUser = useCallback(async (id: string) => {
    await api.delete(`/masters/users/${id}`);
  }, [api]);

  /** パスワード初期化（リセット） */
  const resetPassword = useCallback(async (id: string) => {
    const response = await api.post<UserMasterInfo>(`/masters/users/${id}/reset-password`);
    return response.data;
  }, [api]);

  const generateLoginId = useCallback(async (role: string, masterCode: string) => {
    // クエリパラメータとして送信
    const response = await api.get<{ loginId: string }>('/masters/users/loginId', {
      params: { role, masterCode }
    });
    return response.data.loginId;
  }, [api]);

  return { getUsersWithProfile, createUser, updateUser, deleteUser, resetPassword, generateLoginId };
};

/**
 * ユーザーマスタ取得クエリ
 * キャッシュ時間を5分に設定し、ログイン状態に応じてデータの取得を行います。
 * @param isLoggedIn ログイン状態フラグ（trueの場合のみクエリを実行）
 */
export const useUserMasterQuery = (isLoggedIn: boolean) => {
  const { getUsersWithProfile } = useUsersApi();
  return useQuery({
    queryKey: USER_LIST_KEY,
    queryFn: getUsersWithProfile,
    enabled: isLoggedIn,
    staleTime: 1000 * 60 * 5, // 5分間はデータを最新とみなす
  });
};

/**
 * ユーザーマスタ更新系ミューテーション
 * データの変更（登録・更新・削除・初期化）と、それに伴うキャッシュの無効化、通知処理を管理します。
 */
export const useUserMasterMutations = () => {
  const queryClient = useQueryClient();
  const api = useUsersApi();

  /** * 新規登録ミューテーション
   * 成功時、登録したログインIDを含む成功通知を表示し、一覧キャッシュを更新します。
   */
  const createMutation = useMutation({
    mutationFn: api.createUser,
    onSuccess: (data) => {
      toast.success(`ユーザー ${data.loginId} を登録しました。`);
      queryClient.invalidateQueries({ queryKey: USER_LIST_KEY });
    },
    onError: (err) => showApiErrorToast(err, "登録に失敗しました。")
  });

  /** * 更新ミューテーション
   * 成功時、一覧キャッシュを更新します。
   */
  const updateMutation = useMutation({
    mutationFn: ({ id, req }: { id: string; req: UserMasterUpdateRequest }) => api.updateUser(id, req),
    onSuccess: (data) => {
      toast.success(`ユーザー ${data.loginId} を更新しました。`);
      queryClient.invalidateQueries({ queryKey: USER_LIST_KEY });
    },
    onError: (err) => showApiErrorToast(err, "更新に失敗しました。")
  });

  /** * 削除ミューテーション
   * 成功時、一覧キャッシュを更新します。
   */
  const deleteMutation = useMutation({
    mutationFn: api.deleteUser,
    onSuccess: () => {
      toast.success("ユーザーを削除しました。");
      queryClient.invalidateQueries({ queryKey: USER_LIST_KEY });
    },
    onError: (err) => showApiErrorToast(err, "削除に失敗しました。")
  });

  /** * パスワード初期化ミューテーション
   * 初期化成功時、一覧キャッシュを最新状態に更新します。
   */
  const resetPasswordMutation = useMutation({
    mutationFn: api.resetPassword,
    onSuccess: () => {
      // 成功通知はPage側のhandleResetPasswordで行うため、ここではキャッシュ更新のみ実施
      queryClient.invalidateQueries({ queryKey: USER_LIST_KEY });
    },
    onError: (err) => showApiErrorToast(err, "パスワード初期化に失敗しました。")
  });

  return { createMutation, updateMutation, deleteMutation, resetPasswordMutation };
};